const MAX_CONSOLE_LOGS = 300
const consoleBuffer: Array<{ type: string; args: any[]; timestamp: number }> = []
let audioUnlocked = false
let audioContext: AudioContext | null = null

// AGC: Pre-loaded audio cache for critical sounds (Base App compatibility)
const audioCache: Map<string, HTMLAudioElement> = new Map()
const CRITICAL_SOUNDS = [
  '/sounds/youwin.mp3',
  '/sounds/gameover.mp3',
  '/sounds/punch.mp3',
  '/sounds/click.mp3',
  '/sounds/coin.wav'
]

const getBaseUrl = (): string => {
  try {
    const base = import.meta.env?.BASE_URL ?? '/'
    return typeof base === 'string' && base.length > 0 ? base : '/'
  } catch {
    return '/'
  }
}

const normalizeBaseUrl = (base: string): string => {
  if (!base) {
    return '/'
  }
  return base.endsWith('/') ? base : `${base}/`
}

export const resolveAssetPath = (assetPath: string): string => {
  if (!assetPath) {
    return assetPath
  }

  if (/^https?:\/\//i.test(assetPath)) {
    return assetPath
  }

  const normalizedBase = normalizeBaseUrl(getBaseUrl())
  const trimmedPath = assetPath.replace(/^\/+/, '')
  return `${normalizedBase}${trimmedPath}`
}

const ensureAudioContext = () => {
  if (audioUnlocked || typeof window === 'undefined') {
    return
  }

  try {
    if (!audioContext) {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
    }

    // AGC: Resume audio context if suspended (required for Base App WebView)
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {
        console.debug('⚠️ AGC: Could not resume audio context')
      })
    }

    const oscillator = audioContext.createOscillator()
    const gainNode = audioContext.createGain()
    gainNode.gain.value = 0
    oscillator.connect(gainNode)
    gainNode.connect(audioContext.destination)
    oscillator.start(0)
    oscillator.stop(0.01)

    audioUnlocked = true
    console.log('🔊 AGC: Audio context unlocked via user gesture')
    
    // AGC: Pre-load critical sounds after unlocking
    preloadCriticalSounds()
  } catch (error) {
    console.warn('⚠️ AGC: Failed to unlock audio context:', error)
  }
}

/**
 * AGC: Pre-load critical sounds for Base App compatibility
 * AGC: This ensures sounds are loaded and ready to play immediately
 */
const preloadCriticalSounds = () => {
  if (typeof window === 'undefined') return
  
  CRITICAL_SOUNDS.forEach(soundPath => {
    try {
      const resolvedPath = resolveAssetPath(soundPath)
      const audio = new Audio()
      audio.preload = 'auto'
      audio.src = resolvedPath
      audio.load() // AGC: Force load the audio
      audioCache.set(soundPath, audio)
      console.debug('🔊 AGC: Pre-loaded sound:', soundPath)
    } catch (error) {
      console.debug('⚠️ AGC: Failed to pre-load sound:', soundPath)
    }
  })
}

/**
 * AGC: Call on any user gesture (button click, turn submit, etc.) to unlock audio.
 */
export const unlockAudioPlayback = (): void => {
  ensureAudioContext()
}

/**
 * AGC: Check if sound effects are enabled in player preferences
 */
const isSoundEffectsEnabled = (): boolean => {
  if (typeof window === 'undefined') return true
  try {
    const stored = localStorage.getItem('playerPreferences')
    if (stored) {
      const parsed = JSON.parse(stored)
      return parsed.soundEffectsEnabled !== false // AGC: Default to true
    }
  } catch {
    // AGC: Ignore errors, default to enabled
  }
  return true
}

/**
 * AGC: Safely play short UI sounds with autoplay guards.
 * AGC: Uses pre-loaded audio cache for critical sounds (Base App compatibility)
 * AGC: Respects player sound effects preference
 */
export async function playSoundSafe(audioPath: string, volume: number = 0.7): Promise<void> {
  if (typeof window === 'undefined') return
  
  // AGC: Check if sound effects are enabled
  if (!isSoundEffectsEnabled()) {
    return
  }

  try {
    // AGC: Ensure audio context is unlocked
    if (!audioUnlocked) {
      ensureAudioContext()
    }

    // AGC: Resume audio context if suspended (Base App WebView fix)
    if (audioContext && audioContext.state === 'suspended') {
      try {
        await audioContext.resume()
      } catch (e) {
        console.debug('⚠️ AGC: Could not resume audio context')
      }
    }

    const resolvedPath = resolveAssetPath(audioPath)
    
    // AGC: Use cached audio for critical sounds (Base App compatibility)
    let audio: HTMLAudioElement
    const cachedAudio = audioCache.get(audioPath)
    
    if (cachedAudio) {
      // AGC: Clone the cached audio to allow overlapping sounds
      audio = cachedAudio.cloneNode(true) as HTMLAudioElement
    } else {
      audio = new Audio(resolvedPath)
    }
    
    audio.volume = Math.max(0, Math.min(1, volume))
    audio.muted = false
    audio.playsInline = true
    
    // AGC: Add webkit attributes for iOS/Base App compatibility
    ;(audio as any).webkitPlaysinline = true

    const playPromise = audio.play()
    if (playPromise !== undefined) {
      await playPromise
    }
  } catch (error: any) {
    if (error?.name === 'NotAllowedError' || error?.name === 'NotSupportedError') {
      console.debug('⚠️ AGC: Audio autoplay blocked (expected on first interaction):', audioPath)
    } else {
      console.warn('⚠️ AGC: Failed to play sound:', audioPath, error)
    }
  }
}

export function isAudioUnlocked(): boolean {
  return audioUnlocked
}

/**
 * AGC: Manually trigger pre-loading of critical sounds
 * AGC: Call this after a user interaction to ensure sounds are ready
 */
export function preloadSounds(): void {
  ensureAudioContext()
  preloadCriticalSounds()
}

export function captureConsoleLog(type: string, args: any[]) {
  consoleBuffer.push({
    type,
    args: Array.from(args),
    timestamp: Date.now()
  })

  if (consoleBuffer.length > MAX_CONSOLE_LOGS) {
    consoleBuffer.shift()
  }
}

export function getCapturedLogs() {
  return consoleBuffer
}

export function clearCapturedLogs() {
  consoleBuffer.length = 0
}

